import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { 
  IconClipboardList, IconTruckDelivery, IconRefresh, 
  IconUsers, IconPlus, IconArrowUpRight, IconArrowDownLeft, IconCheck,
  IconShirt, IconDroplet, IconDeviceDesktop, IconHanger
} from "@tabler/icons-react";
import apiClient from "../helpers/ApiClient";

export default function LaundryPage() {
  const [tab, setTab] = useState("inventory"); 
  // inventory, usage, dispatches, vendors, guest_orders, batches, machines, uniforms
  const [loading, setLoading] = useState(false);

  // Data
  const [items, setItems] = useState([]);
  const [usageLogs, setUsageLogs] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [guestOrders, setGuestOrders] = useState([]);
  const [batches, setBatches] = useState([]);
  const [machines, setMachines] = useState([]);
  const [chemicals, setChemicals] = useState([]);
  const [uniforms, setUniforms] = useState([]);

  // Modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("");

  const [newItemForm, setNewItemForm] = useState({ name: "", stock: 0, threshold: 0, category: "General", par_level: 0 });
  const [newUsageForm, setNewUsageForm] = useState({ roomId: "", roomName: "", type: "Issue", staffName: "", selectedItemId: "", quantity: "" });
  const [newVendorForm, setNewVendorForm] = useState({ name: "", phone: "", address: "" });
  const [newDispatchForm, setNewDispatchForm] = useState({ vendorId: "", dispatchDate: new Date().toISOString().slice(0, 16), selectedItemId: "", quantity: "" });
  
  const [newGuestOrderForm, setNewGuestOrderForm] = useState({ room_id: "", guest_name: "", items: "", total_price: 0 });
  const [newBatchForm, setNewBatchForm] = useState({ batch_type: "Washing", weight_kg: 0, machine_id: "" });
  const [newMachineForm, setNewMachineForm] = useState({ name: "", type: "Washer" });
  const [newChemicalForm, setNewChemicalForm] = useState({ name: "", stock: 0, unit: "Liters" });
  const [newUniformForm, setNewUniformForm] = useState({ staff_name: "", uniform_type: "" });

  useEffect(() => {
    fetchTabData();
  }, [tab]);

  const fetchTabData = async () => {
    setLoading(true);
    try {
      if (tab === "inventory") {
        const res = await apiClient.get("/laundry/items");
        setItems(res.data);
      } else if (tab === "usage") {
        const [usageRes, itemsRes] = await Promise.all([ apiClient.get("/laundry/usage"), apiClient.get("/laundry/items") ]);
        setUsageLogs(usageRes.data);
        setItems(itemsRes.data);
      } else if (tab === "dispatches") {
        const [dispRes, vendRes, itemsRes] = await Promise.all([ apiClient.get("/laundry/dispatches"), apiClient.get("/laundry/vendors"), apiClient.get("/laundry/items") ]);
        setDispatches(dispRes.data);
        setVendors(vendRes.data);
        setItems(itemsRes.data);
      } else if (tab === "vendors") {
        const res = await apiClient.get("/laundry/vendors");
        setVendors(res.data);
      } else if (tab === "guest_orders") {
        const res = await apiClient.get("/laundry/guest-orders");
        setGuestOrders(res.data);
      } else if (tab === "batches") {
        const [batchRes, machRes] = await Promise.all([ apiClient.get("/laundry/batches"), apiClient.get("/laundry/machines") ]);
        setBatches(batchRes.data);
        setMachines(machRes.data);
      } else if (tab === "machines") {
        const [machRes, chemRes] = await Promise.all([ apiClient.get("/laundry/machines"), apiClient.get("/laundry/chemicals") ]);
        setMachines(machRes.data);
        setChemicals(chemRes.data);
      } else if (tab === "uniforms") {
        const res = await apiClient.get("/laundry/uniforms");
        setUniforms(res.data);
      }
    } catch (e) {
      toast.error("Failed to load laundry data");
    } finally {
      setLoading(false);
    }
  };

  const handleGeneralSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalType === "inventory") {
        await apiClient.post("/laundry/items", newItemForm);
        toast.success("Item added");
      } else if (modalType === "usage") {
        const item = items.find(i => i.id == newUsageForm.selectedItemId);
        await apiClient.post("/laundry/usage", {
          roomName: newUsageForm.roomName, type: newUsageForm.type, staffName: newUsageForm.staffName,
          items: [{ id: item.id, name: item.name, quantity: newUsageForm.quantity }]
        });
        toast.success("Usage logged");
      } else if (modalType === "vendor") {
        await apiClient.post("/laundry/vendors", newVendorForm);
        toast.success("Vendor registered");
      } else if (modalType === "dispatch") {
        const item = items.find(i => i.id == newDispatchForm.selectedItemId);
        await apiClient.post("/laundry/dispatches", {
          vendorId: newDispatchForm.vendorId, dispatchDate: newDispatchForm.dispatchDate,
          items: [{ id: item.id, name: item.name, quantity: newDispatchForm.quantity }]
        });
        toast.success("Dispatch logged");
      } else if (modalType === "guest_order") {
        await apiClient.post("/laundry/guest-orders", newGuestOrderForm);
        toast.success("Guest order created");
      } else if (modalType === "batch") {
        await apiClient.post("/laundry/batches", newBatchForm);
        toast.success("Batch logged");
      } else if (modalType === "machine") {
        await apiClient.post("/laundry/machines", newMachineForm);
        toast.success("Machine added");
      } else if (modalType === "chemical") {
        await apiClient.post("/laundry/chemicals", newChemicalForm);
        toast.success("Chemical added");
      } else if (modalType === "uniform") {
        await apiClient.post("/laundry/uniforms", newUniformForm);
        toast.success("Uniform issued");
      }
      setIsModalOpen(false);
      fetchTabData();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save record");
    }
  };

  const updateStatus = async (type, id, status) => {
    try {
      if(type === "dispatch") await apiClient.patch(`/laundry/dispatches/${id}/return`, { return_date: new Date().toISOString().slice(0,19).replace('T',' ') });
      else if(type === "guest_order") await apiClient.patch(`/laundry/guest-orders/${id}/status`, { status });
      else if(type === "batch") await apiClient.patch(`/laundry/batches/${id}/status`, { status });
      else if(type === "uniform") await apiClient.patch(`/laundry/uniforms/${id}/status`, { status });
      toast.success("Status updated");
      fetchTabData();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const getStatusColor = (status) => {
    const s = String(status).toLowerCase();
    if (["clean", "ready", "returned", "delivered", "available"].includes(s)) return "bg-green-50 text-green-600";
    if (["dirty", "requested", "washing", "pending", "issued"].includes(s)) return "bg-yellow-50 text-yellow-600";
    if (["drying", "ironing", "collected"].includes(s)) return "bg-blue-50 text-blue-600";
    if (["damaged", "lost", "out of order"].includes(s)) return "bg-red-50 text-red-600";
    return "bg-gray-100 text-gray-600";
  };

  return (
    <div className="p-6 min-h-screen bg-restro-green-light dark:bg-restro-gray text-restro-text dark:text-white">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">Laundry & Linen</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage inventory, guest orders, workflow batches, and uniforms.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => { setModalType(tab==="machines" ? "machine" : tab==="inventory"?"inventory":tab==="usage"?"usage":tab==="vendors"?"vendor":tab==="guest_orders"?"guest_order":tab==="batches"?"batch":tab==="uniforms"?"uniform":"dispatch"); setIsModalOpen(true); }}
            className="px-5 py-3 rounded-2xl bg-restro-green hover:bg-restro-green-button-hover text-white font-bold flex items-center gap-2 shadow-sm transition">
            <IconPlus size={18} /> Add Record
          </button>
          {tab === "machines" && (
            <button onClick={() => { setModalType("chemical"); setIsModalOpen(true); }} className="px-5 py-3 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-bold flex items-center gap-2 shadow-sm transition">
              <IconPlus size={18} /> Add Chemical
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100 dark:border-zinc-800 gap-6 mb-8 overflow-x-auto">
        {[
          { id: "inventory", label: "Linen Inventory", icon: <IconClipboardList size={18} /> },
          { id: "usage", label: "Room Usage", icon: <IconRefresh size={18} /> },
          { id: "guest_orders", label: "Guest Orders", icon: <IconShirt size={18} /> },
          { id: "batches", label: "Laundry Batches", icon: <IconDroplet size={18} /> },
          { id: "dispatches", label: "Vendor Dispatches", icon: <IconTruckDelivery size={18} /> },
          { id: "machines", label: "Machines & Chemicals", icon: <IconDeviceDesktop size={18} /> },
          { id: "uniforms", label: "Staff Uniforms", icon: <IconHanger size={18} /> },
          { id: "vendors", label: "Vendors", icon: <IconUsers size={18} /> }
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`py-4 px-2 text-sm font-semibold border-b-2 -mb-px flex items-center gap-2 whitespace-nowrap transition ${tab === t.id ? "border-restro-green text-restro-green" : "border-transparent text-gray-400"}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-restro-green"></div></div>
      ) : (
        <div className="bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 rounded-3xl shadow-sm overflow-hidden p-0">
          
          {tab === "inventory" && (
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 dark:bg-zinc-800"><tr className="border-b border-gray-100 dark:border-zinc-800">
                <th className="p-4 font-bold text-gray-500 uppercase">Item</th>
                <th className="p-4 font-bold text-gray-500 uppercase">Category</th>
                <th className="p-4 font-bold text-gray-500 uppercase">Stock</th>
                <th className="p-4 font-bold text-gray-500 uppercase">PAR Level</th>
                <th className="p-4 font-bold text-gray-500 uppercase">Status</th>
              </tr></thead>
              <tbody>{items.map(item => (
                <tr key={item.id} className="border-b border-gray-50 dark:border-zinc-800/50">
                  <td className="p-4 font-bold text-gray-900 dark:text-white">{item.name}</td>
                  <td className="p-4 text-gray-500">{item.category}</td>
                  <td className="p-4 font-bold">{item.stock}</td>
                  <td className="p-4 text-gray-500">{item.par_level || 0}</td>
                  <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(item.status || 'Available')}`}>{item.status || 'Available'}</span></td>
                </tr>
              ))}</tbody>
            </table>
          )}

          {tab === "guest_orders" && (
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 dark:bg-zinc-800"><tr className="border-b border-gray-100 dark:border-zinc-800">
                <th className="p-4 font-bold text-gray-500 uppercase">Guest</th>
                <th className="p-4 font-bold text-gray-500 uppercase">Items</th>
                <th className="p-4 font-bold text-gray-500 uppercase">Price</th>
                <th className="p-4 font-bold text-gray-500 uppercase">Status</th>
                <th className="p-4 font-bold text-gray-500 uppercase text-right">Update</th>
              </tr></thead>
              <tbody>{guestOrders.map(order => (
                <tr key={order.id} className="border-b border-gray-50 dark:border-zinc-800/50">
                  <td className="p-4 font-bold text-gray-900 dark:text-white">{order.guest_name} <span className="text-xs text-gray-400">({order.room_name})</span></td>
                  <td className="p-4 text-gray-500">{order.items}</td>
                  <td className="p-4 font-bold">${order.total_price}</td>
                  <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(order.status)}`}>{order.status}</span></td>
                  <td className="p-4 text-right">
                    <select onChange={(e) => updateStatus("guest_order", order.id, e.target.value)} value={order.status} className="p-1 rounded bg-gray-100 text-xs font-bold focus:outline-none">
                      <option value="Requested">Requested</option>
                      <option value="Collected">Collected</option>
                      <option value="Washing">Washing</option>
                      <option value="Drying">Drying</option>
                      <option value="Ready">Ready</option>
                      <option value="Delivered">Delivered</option>
                    </select>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}

          {tab === "batches" && (
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 dark:bg-zinc-800"><tr className="border-b border-gray-100 dark:border-zinc-800">
                <th className="p-4 font-bold text-gray-500 uppercase">Batch Type</th>
                <th className="p-4 font-bold text-gray-500 uppercase">Machine</th>
                <th className="p-4 font-bold text-gray-500 uppercase">Weight</th>
                <th className="p-4 font-bold text-gray-500 uppercase">Status</th>
                <th className="p-4 font-bold text-gray-500 uppercase text-right">Actions</th>
              </tr></thead>
              <tbody>{batches.map(b => (
                <tr key={b.id} className="border-b border-gray-50 dark:border-zinc-800/50">
                  <td className="p-4 font-bold text-gray-900 dark:text-white">{b.batch_type}</td>
                  <td className="p-4 text-gray-500">{b.machine_name || "N/A"}</td>
                  <td className="p-4 text-gray-500">{b.weight_kg} kg</td>
                  <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(b.status)}`}>{b.status}</span></td>
                  <td className="p-4 text-right">
                    <select onChange={(e) => updateStatus("batch", b.id, e.target.value)} value={b.status} className="p-1 rounded bg-gray-100 text-xs font-bold focus:outline-none">
                      <option value="Washing">Washing</option>
                      <option value="Drying">Drying</option>
                      <option value="Ironing">Ironing</option>
                      <option value="Ready">Ready</option>
                    </select>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}

          {tab === "machines" && (
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div>
                <h3 className="p-4 font-bold text-gray-500 uppercase text-[11px] bg-gray-50 dark:bg-zinc-800">Machines</h3>
                <table className="w-full text-left text-sm">
                  <tbody>{machines.map(m => (
                    <tr key={m.id} className="border-b border-gray-50 dark:border-zinc-800/50">
                      <td className="p-4 font-bold text-gray-900 dark:text-white">{m.name}</td>
                      <td className="p-4 text-gray-500">{m.type}</td>
                      <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(m.status)}`}>{m.status}</span></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
              <div className="border-l border-gray-100 dark:border-zinc-800">
                <h3 className="p-4 font-bold text-gray-500 uppercase text-[11px] bg-gray-50 dark:bg-zinc-800">Chemicals Inventory</h3>
                <table className="w-full text-left text-sm">
                  <tbody>{chemicals.map(c => (
                    <tr key={c.id} className="border-b border-gray-50 dark:border-zinc-800/50">
                      <td className="p-4 font-bold text-gray-900 dark:text-white">{c.name}</td>
                      <td className="p-4 text-gray-500 font-bold">{c.stock} {c.unit}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "uniforms" && (
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 dark:bg-zinc-800"><tr className="border-b border-gray-100 dark:border-zinc-800">
                <th className="p-4 font-bold text-gray-500 uppercase">Staff Name</th>
                <th className="p-4 font-bold text-gray-500 uppercase">Uniform Type</th>
                <th className="p-4 font-bold text-gray-500 uppercase">Status</th>
                <th className="p-4 font-bold text-gray-500 uppercase text-right">Actions</th>
              </tr></thead>
              <tbody>{uniforms.map(u => (
                <tr key={u.id} className="border-b border-gray-50 dark:border-zinc-800/50">
                  <td className="p-4 font-bold text-gray-900 dark:text-white">{u.staff_name}</td>
                  <td className="p-4 text-gray-500">{u.uniform_type}</td>
                  <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(u.status)}`}>{u.status}</span></td>
                  <td className="p-4 text-right">
                    <select onChange={(e) => updateStatus("uniform", u.id, e.target.value)} value={u.status} className="p-1 rounded bg-gray-100 text-xs font-bold focus:outline-none">
                      <option value="Issued">Issued</option>
                      <option value="Returned">Returned</option>
                      <option value="Damaged">Damaged</option>
                      <option value="Lost">Lost</option>
                    </select>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}

        </div>
      )}

      {/* Unified Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white dark:bg-zinc-950 border border-gray-100 dark:border-zinc-800 rounded-3xl w-full max-w-md shadow-2xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold dark:text-white capitalize">Add {modalType.replace('_', ' ')}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <form onSubmit={handleGeneralSubmit} className="space-y-4">
              
              {modalType === "guest_order" && (
                <>
                  <input type="text" placeholder="Room Number (optional)" value={newGuestOrderForm.room_id} onChange={e=>setNewGuestOrderForm({...newGuestOrderForm, room_id: e.target.value})} className="w-full p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                  <input type="text" required placeholder="Guest Name" value={newGuestOrderForm.guest_name} onChange={e=>setNewGuestOrderForm({...newGuestOrderForm, guest_name: e.target.value})} className="w-full p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                  <input type="text" required placeholder="Items (e.g. 2 Shirts, 1 Trouser)" value={newGuestOrderForm.items} onChange={e=>setNewGuestOrderForm({...newGuestOrderForm, items: e.target.value})} className="w-full p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                  <input type="number" required placeholder="Total Price" value={newGuestOrderForm.total_price} onChange={e=>setNewGuestOrderForm({...newGuestOrderForm, total_price: e.target.value})} className="w-full p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                </>
              )}

              {modalType === "batch" && (
                <>
                  <select value={newBatchForm.batch_type} onChange={e=>setNewBatchForm({...newBatchForm, batch_type: e.target.value})} className="w-full p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm">
                    <option value="Washing">Washing</option><option value="Drying">Drying</option><option value="Ironing">Ironing</option>
                  </select>
                  <input type="number" required placeholder="Weight (kg)" value={newBatchForm.weight_kg} onChange={e=>setNewBatchForm({...newBatchForm, weight_kg: e.target.value})} className="w-full p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                  <select value={newBatchForm.machine_id} onChange={e=>setNewBatchForm({...newBatchForm, machine_id: e.target.value})} className="w-full p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm">
                    <option value="">Select Machine...</option>
                    {machines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </>
              )}

              {modalType === "uniform" && (
                <>
                  <input type="text" required placeholder="Staff Name" value={newUniformForm.staff_name} onChange={e=>setNewUniformForm({...newUniformForm, staff_name: e.target.value})} className="w-full p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                  <input type="text" required placeholder="Uniform Type" value={newUniformForm.uniform_type} onChange={e=>setNewUniformForm({...newUniformForm, uniform_type: e.target.value})} className="w-full p-3.5 rounded-xl border border-gray-200 dark:border-zinc-800 bg-transparent text-sm focus:outline-none" />
                </>
              )}

              <button type="submit" className="w-full py-4 rounded-xl font-bold bg-restro-green hover:bg-restro-green-button-hover text-white transition">Save Record</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
